/**
 * Zone Store — zone layout presets and active zone state
 * Zone data shared between Project Settings dialog, Properties Transform tab, and Program monitor
 */
import { createStore } from 'zustand/vanilla';
import { eventBus } from '../engine/eventBus';

// ── Zone Layout Data ────────────────────────────────────────

export interface ZoneRect {
  id: number;
  x: number;      // px from left (within 1280x720 design space)
  y: number;      // px from top
  w: number;      // width px
  h: number;      // height px
  color: string;
  name: string;
}

export interface ZonePreset {
  name: string;
  zones: ZoneRect[];
}

export const DESIGN_W = 1280;
export const DESIGN_H = 720;

/**
 * All zone rects are defined in 1280x720 coordinates.
 * Every preset tiles the full 1280x720 area with zero gaps.
 */
export const ZONE_PRESETS: ZonePreset[] = [
  {
    name: 'Full Layout -- No Zones',
    zones: [
      { id: 1, x: 0, y: 0, w: 1280, h: 720, color: '#4444cc', name: 'Main Zone' },
    ],
  },
  {
    name: '6 Up Single',
    zones: [
      { id: 1, x: 0,    y: 0,   w: 256, h: 234, color: '#4a9a4a', name: 'Zone 1' },
      { id: 2, x: 0,    y: 234, w: 256, h: 234, color: '#2a7a6a', name: 'Zone 2' },
      { id: 5, x: 256,  y: 0,   w: 768, h: 468, color: '#44aacc', name: 'Main Zone' },
      { id: 3, x: 1024, y: 0,   w: 256, h: 234, color: '#cc4488', name: 'Zone 3' },
      { id: 4, x: 1024, y: 234, w: 256, h: 234, color: '#c8b832', name: 'Zone 4' },
      { id: 6, x: 0,    y: 468, w: 1280, h: 252, color: '#cc7744', name: 'Bottom Zone' },
    ],
  },
  {
    name: 'Horizontal Split',
    zones: [
      { id: 1, x: 0,   y: 0, w: 640, h: 720, color: '#4444cc', name: 'Left' },
      { id: 2, x: 640, y: 0, w: 640, h: 720, color: '#44cc88', name: 'Right' },
    ],
  },
  {
    name: 'Quads',
    zones: [
      { id: 1, x: 0,   y: 0,   w: 640, h: 360, color: '#4444cc', name: 'Top Left' },
      { id: 2, x: 640, y: 0,   w: 640, h: 360, color: '#44cc88', name: 'Top Right' },
      { id: 3, x: 0,   y: 360, w: 640, h: 360, color: '#cc4488', name: 'Bottom Left' },
      { id: 4, x: 640, y: 360, w: 640, h: 360, color: '#c8b832', name: 'Bottom Right' },
    ],
  },
  {
    name: 'Standard Scoreboard B',
    zones: [
      { id: 1, x: 0,    y: 0,   w: 160, h: 200, color: '#4a7c4a', name: 'Ad Left Top' },
      { id: 2, x: 0,    y: 200, w: 160, h: 180, color: '#7b4a7b', name: 'Ad Left Mid' },
      { id: 3, x: 0,    y: 380, w: 160, h: 160, color: '#cc4444', name: 'Ad Left Bot' },
      { id: 7, x: 160,  y: 0,   w: 960, h: 540, color: '#4444cc', name: 'Main Zone' },
      { id: 4, x: 1120, y: 0,   w: 160, h: 200, color: '#c8b832', name: 'Ad Right Top' },
      { id: 5, x: 1120, y: 200, w: 160, h: 180, color: '#2a8a8a', name: 'Ad Right Mid' },
      { id: 6, x: 1120, y: 380, w: 160, h: 160, color: '#cc8844', name: 'Ad Right Bot' },
      { id: 8, x: 0,    y: 540, w: 1280, h: 180, color: '#22aa88', name: 'Info Zone' },
    ],
  },
];

// ── Store ────────────────────────────────────────────────────

export interface VideoFormat {
  width: number;
  height: number;
  fps: number;
  interlaced: boolean;
}

export interface ZoneStoreState {
  activePresetIndex: number;
  videoFormat: VideoFormat;
  // Actions
  getActivePreset: () => ZonePreset;
  setActivePreset: (index: number) => void;
  setVideoFormat: (fmt: VideoFormat) => void;
  getZoneById: (zoneId: number) => ZoneRect | undefined;
  getLargestZoneId: () => number;
}

export const zoneStore = createStore<ZoneStoreState>((set, get) => ({
  activePresetIndex: 0,
  videoFormat: { width: 1920, height: 1080, fps: 59.94, interlaced: true },

  getActivePreset: () => ZONE_PRESETS[get().activePresetIndex] || ZONE_PRESETS[0],

  setActivePreset: (index) => {
    if (index >= 0 && index < ZONE_PRESETS.length) {
      set({ activePresetIndex: index });
      eventBus.emit('zone:presetChanged', { presetIndex: index });
    }
  },

  setVideoFormat: (fmt) => {
    set({ videoFormat: fmt });
    eventBus.emit('zone:presetChanged', { presetIndex: get().activePresetIndex });
  },

  getZoneById: (zoneId) => {
    const preset = get().getActivePreset();
    return preset.zones.find((z) => z.id === zoneId);
  },

  getLargestZoneId: () => {
    const preset = get().getActivePreset();
    let largest = preset.zones[0];
    for (const z of preset.zones) {
      if (z.w * z.h > largest.w * largest.h) largest = z;
    }
    return largest.id;
  },
}));

// ── Shared rendering helper ──────────────────────────────────

/** Render a zone diagram as HTML (shared between Project Settings and Zone Assignment popup) */
export function renderZoneDiagram(preset: ZonePreset): string {
  const zoneEls = preset.zones.map((z) => {
    const left = (z.x / DESIGN_W * 100).toFixed(4);
    const top = (z.y / DESIGN_H * 100).toFixed(4);
    const width = (z.w / DESIGN_W * 100).toFixed(4);
    const height = (z.h / DESIGN_H * 100).toFixed(4);
    return `<div class="ps-zone" style="left:${left}%;top:${top}%;width:${width}%;height:${height}%;background:${z.color};">
      <span class="ps-zone-num">${z.id}</span>
    </div>`;
  }).join('');

  return `<div class="ps-zone-container"><div class="ps-zone-area">${zoneEls}</div></div>`;
}
